import { getPartnerToken, getPartnerRefresh, clearPartnerSession, partnerTokenClaims } from '@/services/auth/partnerSessionStorage';
import { createWebHistory, createRouter } from "vue-router";
import { canAccessTpvPermission, getTpvPermissionForPath } from "@/utils/tpv_permissions.js";
import axios from "axios";
import {
  getPartnerProfile,
  isPartnerProfileFresh,
} from "@/services/auth/partnerProfileService";
import store from "../store";
import { isTpvPermissionDisabledOffline } from "@/utils/tpv_offline_policy";
import {
  enableManualOfflineMode,
  getConnectivityState,
  isBackendUnavailableError,
  markBackendUnavailable,
  markBackendAvailable,
  retryConnectivity,
  shouldUseOfflineMode,
} from "@/services/connectivity/connectivityService";
import { getPartnerSessionCache } from "@/services/offline/partnerSessionCache";
import { adminAiGet } from "@/services/ai/adminAiClient";
import { hydrateOfflineSessionStore, getOfflineSession, isOfflineSessionActive } from "@/services/offline/offlineUserAccess";
import { OFFLINE_MODE_ENABLED } from "@/services/offline/offlineFeature";
import { applyPwaUpdate, shouldReloadOnNavigation } from "@/utils/pwa";
//import store from '../store';

const Home = () => import("../views/Home.vue");
// Login es la ruta de arranque de la PWA. Mantenerla en el bundle principal
// evita que una instalación que se abre sin red dependa de un chunk que aún
// no haya podido cachearse.
import Login from "../views/Login.vue";
const Carta = () => import("../views/Carta.vue");
const WalletPromotions = () => import("../views/WalletPromotions.vue");
const WalletPersonal = () => import("../views/WalletPersonal.vue");
const LoyaltyPrivacy = () => import("../views/LoyaltyPrivacy.vue");
const Cuenta = () => import("../views/Cuenta.vue");
const Carrito = () => import("../views/Carrito.vue");
const PanelComandas = () => import("../views/PanelComandas.vue");
const PanelMesas = () => import("../views/PanelMesas.vue");
const AccesoMesas = () => import("../views/AccesoMesas_old.vue");
const ChangePass = () => import("../views/ChangePass.vue");
const MisPedidos = () => import("../views/MisPedidos.vue");
const PagoRedsys = () => import("../views/PagoRedsys.vue");
const LoginQr = () => import("../views/LoginQr.vue");
const LoginDelivery = () => import("../views/LoginDelivery.vue");
const LoginTakeAway = () => import("../views/LoginTakeAway.vue");
const LoginBarra = () => import("../views/LoginBarra.vue");
const LoginQuiosco = () => import("../views/LoginQuiosco.vue");
const Unauthorized = () => import("../views/Unauthorized.vue");
const PartnerSettings = () => import("../views/PartnerSettings.vue");
const GestionMesas = () => import("../views/GestionMesas.vue");
const GestionCamareros = () => import("../views/GestionCamareros.vue");
const GestionCarta = () => import("../views/GestionCarta.vue");
const CartaRapida = () => import("../views/CartaRapida.vue");
const DashboardPartner = () => import("../views/DashboardPartner.vue");
const LoginAdmin = () => import("../views/LoginAdmin.vue");
const AIAdminDashboard = () => import("../views/AIAdminDashboard.vue");
const AdminMenuImport = () => import("../views/AdminMenuImport.vue");
const InteligenciaArtificial = () => import("../views/InteligenciaArtificial.vue");
const MandarIncidencias = () => import("../views/MandarIncidencias.vue");
const Fidelizacion = () => import("../views/Fidelizacion.vue");
const ClientUpdateOrder = () => import("../views/ClientUpdateOrder.vue");
const PoliticaPrivacidad = () => import("../views/PrivacyPolicy.vue");
const TPVHub = () => import("../views/TPVHub.vue");
const CajaPartner = () => import("../views/CajaPartner.vue");
const PuntoDeVenta = () => import("../views/PuntoDeVenta.vue");
const ReservasPartner = () => import("../views/ReservasPartner.vue");
const ReservasGestion = () => import("../views/ReservasGestion.vue");
const ReservasPublicas = () => import("../views/ReservasPublicas.vue");
const ReservasCancelacion = () => import("../views/ReservasCancelacion.vue");
const HistorialPedidos = () => import("../views/HistorialPedidos.vue");
const ClientesHabituales = () => import("../views/ClientesHabituales.vue");
const ControlHorario = () => import("../views/ControlHorario.vue");
const FacturasPartner = () => import("../views/FacturasPartner.vue");
const OfflineSectionDisabled = () => import("../views/OfflineSectionDisabled.vue");
const SincronizacionLocal = () => import("../views/SincronizacionLocal.vue");
const EnsayoLocal = () => import("../views/EnsayoLocal.vue");

// Chunks que deben quedar disponibles para que el TPV arranque y pueda
// navegar con el Hub cuando se detiene el servidor de desarrollo. Se cargan
// en segundo plano después de que el Service Worker ya esté activo.
const criticalOfflineRouteComponents = [
  Home,
  TPVHub,
  PuntoDeVenta,
  PanelComandas,
  PanelMesas,
  GestionMesas,
  GestionCarta,
  HistorialPedidos,
  SincronizacionLocal,
  CajaPartner,
];

export function preloadCriticalRouteChunks() {
  return Promise.allSettled(
    criticalOfflineRouteComponents.map((component) => (
      typeof component === 'function' ? component() : component
    )),
  );
}


const routes = [
  {
    path: "/",
    redirect: "/Login",
  },
  {
    path: "/PoliticaPrivacidad",
    name: "PoliticaPrivacidad",
    component: PoliticaPrivacidad,
    meta: { showFooter: false, authorized: false, fullpage: true },
  },
  {
    path: "/reservas/cancelar/:token",
    name: "ReservasCancelacion",
    component: ReservasCancelacion,
    meta: { showFooter: false, authorized: false, fullpage: true },
  },
  {
    path: "/reservas/:restaurantSlug",
    name: "ReservasPublicas",
    component: ReservasPublicas,
    meta: { showFooter: false, authorized: false, fullpage: true },
  },
  {
    path: "/PartnerSettings",
    name: "PartnerSettings",
    component: PartnerSettings,
    meta: { showFooter: false, authorized: false, partnerMenu: true },
  },
  {
    path: "/Home",
    name: "Home",
    component: Home,
    meta: { showFooter: true, authorized: true },
  },
  {
    path: "/Carta/:restaurant",
    name: "Carta",
    component: Carta,
    meta: { showFooter: false, authorized: false, fullpage: true },
  },
  {
    path: "/MiFidelidad/:serial",
    name: "WalletPersonal",
    component: WalletPersonal,
    meta: { showFooter: false, authorized: false, fullpage: true, noTranslate: true },
  },
  {
    path: "/Fidelidad/:restaurant/Privacidad",
    name: "LoyaltyPrivacy",
    component: LoyaltyPrivacy,
    meta: { showFooter: false, authorized: false, fullpage: true, noTranslate: true },
  },
  {
    path: "/Fidelidad/:restaurant",
    name: "WalletPromotions",
    component: WalletPromotions,
    meta: { showFooter: false, authorized: false, fullpage: true, noTranslate: true },
  },
  {
    path: "/Cuenta",
    name: "Cuenta",
    component: Cuenta,
    meta: { showFooter: true, authorized: true },
  },
  {
    path: "/Carrito",
    name: "Carrito",
    component: Carrito,
    meta: { showFooter: true, authorized: true },
  },
  {
    path: "/MisPedidos",
    name: "MisPedidos",
    component: MisPedidos,
    meta: { showFooter: true, authorized: true },
  },
  {
    // Vuelta de Redsys tras un pago a pantalla completa (Bizum).
    path: "/PagoRedsys/:transactionId/:result",
    name: "PagoRedsys",
    component: PagoRedsys,
    meta: { authorized: true },
  },
  {
    path: "/Login",
    name: "Login",
    component: Login,
    meta: { showFooter: false, authorized: false, fullpage: true, noTranslate: true },
  },
  {
    path: "/ChangePass/:token?",
    name: "ChangePass",
    component: ChangePass,
    meta: { showFooter: false, authorized: false },
  },
  {
    path: "/LoginQr/:mesa",
    name: "LoginQr",
    component: LoginQr,
    meta: { showFooter: false, authorized: false, fullpage: true },
  },
  {
    path: "/Delivery/:delivery_name",
    name: "LoginDelivery",
    component: LoginDelivery,
    meta: { showFooter: false, authorized: false, fullpage: true },
  },
  {
    path: "/TakeAway/:delivery_name",
    name: "LoginTakeAway",
    component: LoginTakeAway,
    meta: { showFooter: false, authorized: false, fullpage: true },
  },
  {
    path: "/Barra/:delivery_name/:table_id?",
    name: "LoginBarra",
    component: LoginBarra,
    meta: { showFooter: false, authorized: false, fullpage: true },
  },
  {
    path: "/Quiosco/:delivery_name",
    name: "LoginQuiosco",
    component: LoginQuiosco,
    meta: { showFooter: false, authorized: false, fullpage: true },
  },
  {
    path: "/Unauthorized",
    name: "Unauthorized",
    component: Unauthorized,
    meta: { partnerMenu: false, showFooter: false, authorized: false, fullpage: true },
  },
  {
    path: "/PanelComandas",
    name: "PanelComandas",
    component: PanelComandas,
    meta: { showFooter: false, authorized: false, fullpage: true, tpvHub: true }
  },
  {
    path: "/PanelMesas",
    name: "PanelMesas",
    component: PanelMesas,
    meta: { showFooter: false, authorized: false, fullpage: true, tpvHub: true }
  },
  {
    path: "/AccesoMesas",
    name: "AccesoMesas",
    component: AccesoMesas,
    meta: { partnerMenu: true, authorized: false }
  },
  {
    path: "/GestionMesas",
    name: "GestionMesas",
    component: GestionMesas,
    meta: { showFooter: false, authorized: false, fullpage: true, tpvHub: true },
  },
  {
    path: "/GestionCamareros",
    name: "GestionCamareros",
    component: GestionCamareros,
    meta: { partnerMenu: true, authorized: false },
  },
  {
    path: "/GestionCarta",
    name: "GestionCarta",
    component: GestionCarta,
    meta: { showFooter: false, authorized: false, fullpage: true, tpvHub: true },
  },
  {
    path: "/CartaRapida",
    name: "CartaRapida",
    component: CartaRapida,
    meta: { showFooter: false, authorized: false, fullpage: true, tpvHub: true },
  },
  {
    path: "/DashboardPartner",
    name: "DashboardPartner",
    component: DashboardPartner,
    meta: { partnerMenu: true, authorized: false },
  },
  {
    path: "/InteligenciaArtificial",
    name: "InteligenciaArtificial",
    component: InteligenciaArtificial,
    meta: { fullpage: true, tpvHub: true, authorized: false },
  },
  {
    path: "/LoginAdmin",
    name: "LoginAdmin",
    component: LoginAdmin,
    meta: { showFooter: false, authorized: false, noTranslate: true },
  },
  {
    path: "/AdminIA",
    name: "AdminIA",
    component: AIAdminDashboard,
    meta: { fullpage: true, adminOnly: true, noTranslate: true },
  },
  {
    path: "/AdminIA/pagos",
    name: "AdminPagos",
    component: AIAdminDashboard,
    meta: { fullpage: true, adminOnly: true, noTranslate: true },
  },
  {
    path: "/AdminIA/ia",
    name: "AdminIAConsumo",
    component: AIAdminDashboard,
    meta: { fullpage: true, adminOnly: true, noTranslate: true },
  },
  {
    path: "/AdminIA/configuracion",
    name: "AdminIAConfiguracion",
    component: AIAdminDashboard,
    meta: { fullpage: true, adminOnly: true, noTranslate: true },
  },
  {
    path: "/AdminIA/restaurante/:restaurantId",
    name: "AdminIARestaurante",
    component: AIAdminDashboard,
    meta: { fullpage: true, adminOnly: true, noTranslate: true },
  },
  {
    path: "/AdminIA/ia/restaurante/:restaurantId",
    name: "AdminIARestauranteIA",
    component: AIAdminDashboard,
    meta: { fullpage: true, adminOnly: true, noTranslate: true },
  },
  {
    path: "/AdminIA/restaurante/:restaurantId/carta",
    name: "AdminCartaIA",
    component: AdminMenuImport,
    meta: { fullpage: true, adminOnly: true, noTranslate: true },
  },
  {
    path: "/MandarIncidencias",
    name: "MandarIncidencias",
    component: MandarIncidencias,
    meta: { partnerMenu: true, authorized: false },
  },
  {
    path: "/Fidelizacion",
    name: "Fidelizacion",
    component: Fidelizacion,
    meta: { partnerMenu: true, authorized: false },
  },
  {
    path: "/ReservasPartner",
    name: "ReservasPartner",
    component: ReservasGestion,
    meta: { showFooter: false, authorized: false, fullpage: true, tpvHub: true },
  },
  {
    path: "/ReservasConfig",
    name: "ReservasConfig",
    component: ReservasPartner,
    meta: { showFooter: false, authorized: false, fullpage: true, tpvHub: true },
  },
  {
    path: "/HistorialPedidos",
    name: "HistorialPedidos",
    component: HistorialPedidos,
    meta: { showFooter: false, authorized: false, fullpage: true, tpvHub: true },
  },
  {
    path: "/ControlHorario",
    name: "ControlHorario",
    component: ControlHorario,
    meta: { showFooter: false, authorized: false, fullpage: true, tpvHub: true },
  },
  {
    path: "/ClientesHabituales",
    name: "ClientesHabituales",
    component: ClientesHabituales,
    meta: { showFooter: false, authorized: false, fullpage: true, tpvHub: true },
  },
  {
    path: '/ClientUpdateOrder',
    name: 'ClientUpdateOrder',
    component: ClientUpdateOrder,
    meta: { authorized: false, showFooter: false, fullpage: true }
  },
  // TPVHub: restored — main hub view after login
  {
    path: '/TPVHub',
    name: 'TPVHub',
    component: TPVHub,
    meta: { showFooter: false, authorized: false, fullpage: true, tpvHub: true }
  },
  {
    path: '/CajaPartner',
    name: 'CajaPartner',
    component: CajaPartner,
    meta: { showFooter: false, authorized: false, fullpage: true, tpvHub: true }
  },
  {
    path: '/FacturasPartner',
    name: 'FacturasPartner',
    component: FacturasPartner,
    meta: { showFooter: false, authorized: false, fullpage: true, tpvHub: true }
  },
  {
    path: '/PuntoDeVenta',
    name: 'PuntoDeVenta',
    component: PuntoDeVenta,
    meta: { showFooter: false, authorized: false, fullpage: true, tpvHub: true }
  },
  {
    path: '/SincronizacionLocal',
    name: 'SincronizacionLocal',
    component: SincronizacionLocal,
    meta: { showFooter: false, authorized: false, fullpage: true, tpvHub: true }
  },
  {
    path: '/EnsayoLocal',
    name: 'EnsayoLocal',
    component: EnsayoLocal,
    meta: { showFooter: false, authorized: false, fullpage: true, tpvHub: true }
  },
  {
    path: '/OfflineNoDisponible',
    name: 'OfflineSectionDisabled',
    component: OfflineSectionDisabled,
    meta: { showFooter: false, authorized: false, fullpage: true, tpvHub: true }
  }
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

if (typeof window !== 'undefined' && /^(127\.0\.0\.1|localhost)$/.test(window.location.hostname)) {
  window.__EWU_E2E__ = {
    ...(window.__EWU_E2E__ || {}),
    connectivity: {
      enableManualOfflineMode,
      getState: getConnectivityState,
      retryConnectivity,
    },
    router,
  };
}

// Versión nueva del front desplegada: la siguiente navegación carga la página
// entera ya actualizada (va antes que el resto de guards y que cargar la vista).
router.beforeEach((to, from) => {
  if (!shouldReloadOnNavigation(from, to)) return true;
  applyPwaUpdate(router.resolve(to).href);
  return false;
});

// Si una vista ya no existe en el servidor (despliegue con la pestaña abierta),
// se carga la página entera en lugar de quedarse en blanco. Una sola vez cada
// 10 s para no entrar en bucle sin conexión.
router.onError((error, to) => {
  const message = `${error?.name || ''} ${error?.message || ''}`;
  if (!to || !/ChunkLoadError|Loading (CSS )?chunk|dynamically imported module|Importing a module script failed/i.test(message)) return;
  try {
    const last = Number(sessionStorage.getItem('ewu-chunk-reload-at') || 0);
    if (Date.now() - last < 10000) return;
    sessionStorage.setItem('ewu-chunk-reload-at', String(Date.now()));
  } catch {
    // Sin sessionStorage se recarga igualmente.
  }
  window.location.assign(router.resolve(to).href);
});

router.beforeEach(async (to, from, next) => {
  //await store.dispatch('initialice');
  window.scrollTo(0, 0);

  if (!OFFLINE_MODE_ENABLED && ['SincronizacionLocal', 'EnsayoLocal', 'OfflineSectionDisabled'].includes(to.name)) {
    return next({ name: 'TPVHub' });
  }

  if (to.meta.noTranslate && document.cookie.includes('googtrans=')) {
    if (sessionStorage.getItem('googtransReset')) {
      sessionStorage.removeItem('googtransReset');
      return next();
    }

    sessionStorage.setItem('googtransReset', 'true');

    // borrar todas las variantes
    document.cookie = `googtrans=; max-age=0; path=/; domain=.ewu.es`;
    document.cookie = `googtrans=; max-age=0; path=/`;

    return next();
  }

  if (to.meta.adminOnly) {
    const adminToken = localStorage.getItem('admin_token');
    if (!adminToken || adminToken === 'null') return next('/Login');
    try {
      await adminAiGet('/budget');
    } catch (error) {
      if (Number(error?.response?.status) === 401) return next('/Login');
      if (Number(error?.response?.status) === 403) return next('/Unauthorized');
    }
  }

  const permission = getTpvPermissionForPath(to.path);

  if (permission && isTpvPermissionDisabledOffline(permission) && shouldUseOfflineMode() && !store.state.device.blockedByDevicePolicy) {
    return next({
      name: 'OfflineSectionDisabled',
      query: { section: permission },
    });
  }

  const requiresPartnerSession = Boolean(to.meta.partnerMenu || to.meta.tpvHub);

  if (requiresPartnerSession) {
    if (OFFLINE_MODE_ENABLED) await hydrateOfflineSessionStore();
    const token = getPartnerToken();
    const refresh = getPartnerRefresh();
    if (partnerTokenClaims(token).token_type === 'partner_selection') return next({ name: 'Login', query: { selectRestaurant: '1' } });
    const offlineSession = OFFLINE_MODE_ENABLED ? getOfflineSession() : null;
    const hasOfflineSession = OFFLINE_MODE_ENABLED && isOfflineSessionActive();
    const cachedPartnerProfile = getPartnerSessionCache(token);

    if ((!token || token === 'null')) {
      if (hasOfflineSession && shouldUseOfflineMode()) {
        if (permission && !canAccessTpvPermission(offlineSession, permission)) {
          return next('/Unauthorized');
        }
        return next();
      }

      return next('/Login');
    }

    if (hasOfflineSession && shouldUseOfflineMode()) {
      if (permission && !canAccessTpvPermission(offlineSession, permission)) {
        return next('/Unauthorized');
      }
      return next();
    }

    if (cachedPartnerProfile) {
      if (!permission || canAccessTpvPermission(cachedPartnerProfile, permission)) {
        if (!isPartnerProfileFresh(cachedPartnerProfile)) {
          void getPartnerProfile({ token, timeoutMs: 5000 }).catch(() => {});
        }
        return next();
      }
    }

    try {
      const partnerProfile = await getPartnerProfile({
        token,
        force: Boolean(cachedPartnerProfile),
        allowStaleFallback: false,
        timeoutMs: 5000,
      });

      if (permission && !canAccessTpvPermission(partnerProfile, permission)) {
        return next('/Unauthorized');
      }
      markBackendAvailable();
    } catch (error) {
      if (isBackendUnavailableError(error)) {
        markBackendUnavailable(error?.message || 'No se pudo comprobar permisos TPV.');
        const cachedUser = offlineSession || cachedPartnerProfile;
        if (cachedUser && permission && !canAccessTpvPermission(cachedUser, permission)) {
          return next('/Unauthorized');
        }
        return next();
      }

      const responseStatus = Number(error?.response?.status);
      const accessCode = error?.response?.data?.detail?.code;
      if (responseStatus === 403 && accessCode === 'restaurant_selection_required') {
        return next({ name: 'Login', query: { selectRestaurant: '1' } });
      }
      if (responseStatus === 403 && accessCode === 'restaurant_access_revoked') {
        store.commit('logout');
        return next('/Login');
      }
      const hasRefresh = refresh && refresh !== 'null';

      if (responseStatus === 401) {
        if (hasRefresh) {
          const accessBeforeRefresh = getPartnerToken();
          try {
            const response2 = await axios.post(
              process.env.VUE_APP_API_URL + '/v1/api/partner/refresh',
              { refresh },
              { headers: { 'Content-Type': 'application/json' } }
            );
            if (getPartnerToken() !== accessBeforeRefresh || getPartnerRefresh() !== refresh) return next(to.fullPath);
            store.commit('login', response2.data);
            if (response2.data.selection_required) return next({ name: 'Login', query: { selectRestaurant: '1' } });
            return next(to.fullPath);
          } catch (refreshError) {
            if (Number(refreshError?.response?.status) !== 401) {
              console.warn('Partner refresh temporarily failed during route guard:', refreshError);
              return next();
            }
            if (getPartnerToken() !== accessBeforeRefresh
              || getPartnerRefresh() !== refresh) {
              return next(to.fullPath);
            }
          }
        }
        clearPartnerSession();
        store.commit('setLoggedIn', false);
        return next('/Login');
      }

      // /partner/me no concede permisos por sección; un 403 al consultar
      // el perfil no prueba que la ruta TPV esté prohibida. Conserva la sesión.
      console.warn('Partner profile check failed; keeping the current session:', error);
      return next();
    }
  }

  next();
});

// eslint-disable-next-line no-redeclare
/* global fbq */

router.afterEach(() => {
  if (window.fbq) {
    fbq('track', 'PageView');
  }
});

export default router;
